import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Loader2, CheckCircle2, AlertCircle, Mail } from 'lucide-react';
import StarsBackground from '@/components/StarsBackground';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

type State = 'loading' | 'valid' | 'already' | 'invalid' | 'submitting' | 'done' | 'error';

export default function Unsubscribe() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [state, setState] = useState<State>('loading');
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) { setState('invalid'); return; }
    (async () => {
      try {
        const res = await fetch(
          `${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${encodeURIComponent(token)}`,
          { headers: { apikey: SUPABASE_ANON_KEY } }
        );
        const data = await res.json();
        if (!res.ok) { setState('invalid'); setError(data?.error ?? 'Invalid link'); return; }
        setEmail(data?.email ?? null);
        setState(data?.alreadyUnsubscribed ? 'already' : 'valid');
      } catch (e) {
        setState('invalid');
        setError(e instanceof Error ? e.message : 'Unable to validate link');
      }
    })();
  }, [token]);

  const confirm = async () => {
    setState('submitting');
    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/handle-email-unsubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', apikey: SUPABASE_ANON_KEY },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!res.ok) { setState('error'); setError(data?.error ?? 'Failed to unsubscribe'); return; }
      setState('done');
    } catch (e) {
      setState('error');
      setError(e instanceof Error ? e.message : 'Failed to unsubscribe');
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      <StarsBackground />
      <Card className="relative z-10 max-w-lg w-full p-8 bg-background/95 backdrop-blur-lg border-primary/20">
        <div className="text-center space-y-5">
          <Mail className="w-12 h-12 text-primary mx-auto" />
          <h1 className="text-2xl font-bold">Email preferences</h1>

          {state === 'loading' && <div className="flex items-center justify-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Validating link…</div>}

          {state === 'valid' && (
            <>
              <p className="text-muted-foreground">
                Unsubscribe {email ? <strong>{email}</strong> : 'this address'} from SOLR app emails (order confirmations, shipping updates, etc.)?
              </p>
              <Button size="lg" onClick={confirm}>Confirm unsubscribe</Button>
            </>
          )}

          {state === 'submitting' && <div className="flex items-center justify-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Unsubscribing…</div>}

          {state === 'done' && (
            <>
              <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
              <p>You've been unsubscribed{email ? `: ${email}` : ''}. We're sorry to see you go.</p>
            </>
          )}

          {state === 'already' && (
            <>
              <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
              <p>{email ?? 'This address'} is already unsubscribed.</p>
            </>
          )}

          {(state === 'invalid' || state === 'error') && (
            <>
              <AlertCircle className="w-12 h-12 text-destructive mx-auto" />
              <p className="text-muted-foreground">{error ?? 'This unsubscribe link is invalid or expired.'}</p>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
